import React, { useState } from 'react';
import { signInAnonymously } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { usePlayer } from '../../context/PlayerContext';
import { useGame } from '../../context/GameContext';
import { useGameRound } from '../../hooks/useGameRound';
import { isValidRoomCode } from '../../utils/roomCodeGen';
import { getBotNames } from '../../utils/botAI';
import Button from '../ui/Button';
import HowToPlay from '../ui/HowToPlay';

function HomeScreen() {
  const [name, setName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [mode, setMode] = useState(null);
  const [botCount, setBotCount] = useState(1);
  const [showHelp, setShowHelp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { setPlayerId, setDisplayName, setIsHost } = usePlayer();
  const { dispatch } = useGame();
  const { createRoom, joinRoom } = useGameRound();

  const handleAuth = async () => {
    const cred = await signInAnonymously(auth);
    return cred.user.uid;
  };

  const handleCreate = async () => {
    if (!name.trim()) return setError("We need a name for the scorecard!");
    setLoading(true);
    setError(null);
    try {
      const uid = await handleAuth();
      const result = await createRoom(name.trim());
      setPlayerId(uid);
      setDisplayName(name.trim());
      setIsHost(true);
      dispatch({ type: 'SET_ROOM_CODE', payload: result.roomCode });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!name.trim()) return setError("We need a name for the scorecard!");
    const code = joinCode.toUpperCase().trim();
    if (!isValidRoomCode(code)) return setError("That code doesn't look right — it's 4 letters");
    setLoading(true);
    setError(null);
    try {
      const uid = await handleAuth();
      await joinRoom(code, name.trim());
      setPlayerId(uid);
      setDisplayName(name.trim());
      setIsHost(false);
      dispatch({ type: 'SET_ROOM_CODE', payload: code });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayBots = async () => {
    if (!name.trim()) return setError("We need a name for the scorecard!");
    setLoading(true);
    setError(null);
    try {
      const uid = await handleAuth();
      const botNames = getBotNames(botCount);
      const fn = httpsCallable(functions, 'createBotRoom');
      const result = await fn({ displayName: name.trim(), botNames });
      setPlayerId(uid);
      setDisplayName(name.trim());
      setIsHost(true);
      dispatch({ type: 'SET_ROOM_CODE', payload: result.data.roomCode });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-screen">
      <h1 className="home-screen__title">⛳️ Golf Card Game</h1>
      <p className="home-screen__subtitle">A game of skill and skill</p>

      <div className="home-screen__input-group">
        <input
          className="home-screen__input"
          type="text"
          placeholder="What do they call you?"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={12}
        />
      </div>

      {!mode && (
        <div className="home-screen__buttons">
          <Button onClick={() => setMode('create')}>Host a Game</Button>
          <Button variant="secondary" onClick={() => setMode('join')}>
            Join Friends
          </Button>
          <Button variant="secondary" onClick={() => setMode('bots')}>
            Challenge the Pro
          </Button>
        </div>
      )}

      {mode === 'create' && (
        <div className="home-screen__action">
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? 'Setting up the course...' : 'Hit the Links'}
          </Button>
          <Button variant="ghost" onClick={() => setMode(null)}>
            Back
          </Button>
        </div>
      )}

      {mode === 'join' && (
        <div className="home-screen__action">
          <input
            className="home-screen__input home-screen__input--code"
            type="text"
            placeholder="Got a code?"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            maxLength={4}
          />
          <Button onClick={handleJoin} disabled={loading}>
            {loading ? 'Walking up to the tee...' : 'Join the Round'}
          </Button>
          <Button variant="ghost" onClick={() => setMode(null)}>
            Back
          </Button>
        </div>
      )}

      {mode === 'bots' && (
        <div className="home-screen__action">
          <div className="home-screen__bot-picker">
            <span>Number of opponents:</span>
            <div className="home-screen__bot-buttons">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  className={`home-screen__bot-btn ${botCount === n ? 'home-screen__bot-btn--active' : ''}`}
                  onClick={() => setBotCount(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <span className="home-screen__bot-names">
              vs {getBotNames(botCount).join(', ')}
            </span>
          </div>
          <Button onClick={handlePlayBots} disabled={loading}>
            {loading ? 'Teeing off...' : "Let's Go!"}
          </Button>
          <Button variant="ghost" onClick={() => setMode(null)}>
            Back
          </Button>
        </div>
      )}

      {error && <div className="home-screen__error">{error}</div>}

      <button className="home-screen__help-btn" onClick={() => setShowHelp(true)}>
        How to Play
      </button>

      {showHelp && <HowToPlay onClose={() => setShowHelp(false)} />}
    </div>
  );
}

export default HomeScreen;
